import {ChartLineUp, Coins, TrendUp} from '../../components/PhosphorIcons'
import {ApexOptions} from 'apexcharts'
import Chart from 'react-apexcharts'
import * as ReactDOMClient from 'react-dom/client'

type CompanyWeightsData = [string, number][]

type StrategyDefinition = {
  href: string
  title: string
  description: string
  annualYield: string
}

const formatterRUB = new Intl.NumberFormat('ru-RU', {
  style: 'currency',
  currency: 'RUB',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const buildProfitSeries = (baseValue: number, rates: number[]) => {
  let accumulated = baseValue
  return rates.map((rate) => {
    accumulated *= rate / 12 + 1
    return accumulated - baseValue + 1
  })
}

const StrategyPerformanceCard = ({
  definition,
  categories,
  profitMargin,
  totalAssets,
}: {
  definition: StrategyDefinition
  categories: string[]
  profitMargin: number[]
  totalAssets: number[]
}) => {
  const profitSeries = buildProfitSeries(100, profitMargin)
  const options: ApexOptions = {
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 164,
      sparkline: {enabled: true},
      toolbar: {show: false},
      zoom: {enabled: false},
    },
    colors: ['#10b981', '#4f46e5'],
    dataLabels: {enabled: false},
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 0.35,
        opacityFrom: 0.38,
        opacityTo: 0.02,
        stops: [0, 90, 100],
      },
    },
    legend: {show: false},
    stroke: {curve: 'smooth', width: [2.5, 2]},
    tooltip: {
      x: {show: true},
      y: [
        {formatter: (value) => `${(value - 1).toFixed(1)}%`},
        {formatter: (value) => formatterRUB.format(value)},
      ],
    },
    xaxis: {categories},
  }

  const total = totalAssets.at(-1) ?? 0

  return (
    <a className='strategy-card' href={definition.href}>
      <div className='strategy-card__header'>
        <div>
          <span className='strategy-card__eyebrow'>Готовый сценарий</span>
          <h2>{definition.title}</h2>
          <p>{definition.description}</p>
        </div>
        <span className='strategy-card__icon'>
          <TrendUp aria-hidden='true' size={20} weight='duotone' />
        </span>
      </div>
      <div className='strategy-card__metrics'>
        <strong>{formatterRUB.format(total)}</strong>
        <span>{definition.annualYield}</span>
      </div>
      <Chart
        height={164}
        options={options}
        series={[
          {name: 'Доход', data: profitSeries},
          {name: 'Сумма', data: totalAssets},
        ]}
        type='area'
      />
    </a>
  )
}

const StrategyStatisticCard = ({title, description}: {title: string; description: string}) => (
  <article className='metric-card'>
    <span className='metric-card__icon'>
      {description.toLocaleLowerCase('ru-RU').includes('стоим') ? (
        <Coins aria-hidden='true' size={20} weight='duotone' />
      ) : (
        <ChartLineUp aria-hidden='true' size={20} weight='duotone' />
      )}
    </span>
    <span className='metric-card__label'>{description}</span>
    <strong>{title || '—'}</strong>
  </article>
)

const limitCompanyWeights = (data: CompanyWeightsData, limit: number): CompanyWeightsData => {
  const sorted = [...data].sort((left, right) => right[1] - left[1])
  if (sorted.length <= limit) {
    return sorted
  }

  const visible = sorted.slice(0, limit)
  visible.push(['Другие', sorted.slice(limit).reduce((sum, item) => sum + item[1], 0)])
  return visible
}

const StrategyPortfolioCard = ({
  title,
  description,
  data,
}: {
  title: string
  description: string
  data: CompanyWeightsData
}) => {
  const limitedData = limitCompanyWeights(data, 6)
  const options: ApexOptions = {
    labels: limitedData.map(([label]) => label),
    colors: ['#6ee7b7', '#34d399', '#10b981', '#059669', '#047857', '#065f46', '#4f46e5'],
    dataLabels: {enabled: false},
    legend: {
      fontFamily: 'inherit',
      fontSize: '12px',
      position: 'right',
    },
    stroke: {show: false},
    tooltip: {
      y: {formatter: (value) => formatterRUB.format(value)},
    },
  }

  return (
    <article className='metric-card metric-card--chart'>
      <div>
        <span className='metric-card__label'>{description}</span>
        <strong>{title}</strong>
      </div>
      <Chart
        height={190}
        options={options}
        series={limitedData.map(([, value]) => value)}
        type='donut'
      />
    </article>
  )
}

const createStrategyRenderers = (definition: StrategyDefinition) => ({
  renderPerformance: (
    root: ReactDOMClient.Root,
    categories: string[],
    totalAssets: number[],
    profitMargin: number[]
  ) => {
    root.render(
      <StrategyPerformanceCard
        categories={categories}
        definition={definition}
        profitMargin={profitMargin}
        totalAssets={totalAssets}
      />
    )
  },
  renderStatistic: (
    root: ReactDOMClient.Root,
    _className: string,
    _svgIcon: string,
    _color: string,
    _iconColor: string,
    _titleColor: string,
    _descriptionColor: string,
    title: string,
    description: string
  ) => {
    root.render(<StrategyStatisticCard description={description} title={title} />)
  },
  renderPortfolio: (
    root: ReactDOMClient.Root,
    _className: string,
    _color: string,
    _titleColor: string,
    _descriptionColor: string,
    title: string,
    description: string,
    data: CompanyWeightsData
  ) => {
    root.render(<StrategyPortfolioCard data={data} description={description} title={title} />)
  },
})

declare global {
  interface Window {
    createRoot: (element: HTMLDivElement) => ReactDOMClient.Root
    renderPeriodicCouponsStrategyWidget: (
      root: ReactDOMClient.Root,
      categories: string[],
      totalAssets: number[],
      profitMargin: number[]
    ) => void
    renderPeriodicCouponsStrategyStatisticsWidget: (
      root: ReactDOMClient.Root,
      className: string,
      svgIcon: string,
      color: string,
      iconColor: string,
      titleColor: string,
      descriptionColor: string,
      title: string,
      description: string
    ) => void
    renderPeriodicCouponsStrategyPortfolioWidget: (
      root: ReactDOMClient.Root,
      className: string,
      color: string,
      titleColor: string,
      descriptionColor: string,
      title: string,
      description: string,
      data: CompanyWeightsData
    ) => void
    renderPeriodicDividendsStrategyWidget: (
      root: ReactDOMClient.Root,
      categories: string[],
      totalAssets: number[],
      profitMargin: number[]
    ) => void
    renderPeriodicDividendsStrategyStatisticsWidget: Window['renderPeriodicCouponsStrategyStatisticsWidget']
    renderPeriodicDividendsStrategyPortfolioWidget: Window['renderPeriodicCouponsStrategyPortfolioWidget']
  }
}

export {createStrategyRenderers}
